import dbClient from './client.js'
import { serialifyRequest, randomId } from '../helpers.js'
import { isCreateOrderRequest, parseApiUrl } from '../helpers.js'

const apiQueue = {}

apiQueue.name = "api_queue"

apiQueue.enqueue = async function (request, locationId) {
  const serialifiedRequest = await serialifyRequest(request)
  return dbClient.open()
    .then(async db => {
      const handle = request.headers.get("X-Upstream-Handle") // Could be null
      const id = randomId()
      const queueRequest = {
        id: id,
        request: serialifiedRequest,
        location_id: locationId,
        date: new Date().toISOString(),
        handle: handle
      }
      await db.put("api_queue", queueRequest)
      return id
    })
}

// Skip a request and its dependents by id
apiQueue.skip = async function (id) {
  const db = await dbClient.open();
  // Get all items in the queue
  const allItems = await db.getAll("api_queue");
  // Find the main item and its dependents
  const mainItem = allItems.find(item => `${item.id}` === `${id}`);
  if (!mainItem) return;
  const dependents = allItems.filter(item => item.handle === `${mainItem.id}`);
  let toSkip = [mainItem, ...dependents];
  // Sort by original date to preserve order
  toSkip.sort((a, b) => new Date(a.date) - new Date(b.date));
  // Delete originals first
  for (const req of toSkip) {
    await db.delete("api_queue", req.id);
  }
  // Prepare new queue items with same ids, handles, and new dates
  const now = new Date();
  const newItems = toSkip.map((req, idx) => ({
    ...req,
    date: new Date(now.getTime() + idx + 1).toISOString() // To ensure order in queue is preserved
  }));
  // Insert new items at the end with the same ids
  for (const newReq of newItems) {
    await db.put("api_queue", newReq);
  }
}

// Delete a request and its dependents by id
apiQueue.delete = async function (id) {
  const db = await dbClient.open();
  // Get all items in the queue
  const allItems = await db.getAll("api_queue");
  // Find the main item and its dependents
  const mainItem = allItems.find(item => `${item.id}` === `${id}`);
  if (!mainItem) return;
  const dependents = allItems.filter(item => item.handle === `${mainItem.id}`);
  // If mainItem is a create order request, delete the associated offline order
  try {
    const method = mainItem.request?.method;
    const url = mainItem.request?.url;
    const parsedUrl = parseApiUrl(url);
    if (isCreateOrderRequest(method, parsedUrl)) {
      await db.delete("offline_orders", mainItem.id);
    }
  } catch (e) { /* ignore */ }
  // Delete dependents first
  for (const dep of dependents) {
    await db.delete("api_queue", dep.id);
  }
  // Delete the main request
  await db.delete("api_queue", mainItem.id);
}

export default apiQueue